import type { Country, FindingCategory, FindingSeverity } from "./types.js";

export type RuleId =
  | "private_dictionary"
  | "email_syntax"
  | "iban_mod97"
  | "payment_card_luhn"
  | "bearer_token"
  | "jwt_shape"
  | "secret_assignment"
  | "pem_private_key"
  | "se_personnummer"
  | "se_samordningsnummer"
  | "dk_cpr_date"
  | "lt_personal_code";

export interface RuleRef {
  id: RuleId;
  version: number;
}

export interface RuleDefinition extends RuleRef {
  country: Country;
  category: FindingCategory;
  severity: FindingSeverity;
  /** A rule version changes whenever externally observable detection behavior changes. */
  changePolicy: "increment-on-behavior-change";
  evidenceKind: "pattern" | "structural" | "dictionary" | "secret";
  requiredStrata: readonly string[];
}

/**
 * The complete alpha detector catalog. The integer is per-rule and monotonic:
 * do not reuse a prior version for changed behavior or changed evidence.
 */
export const RULE_CATALOG = {
  private_dictionary: { id: "private_dictionary", version: 1, country: "core", category: "dictionary", severity: "critical", changePolicy: "increment-on-behavior-change", evidenceKind: "dictionary", requiredStrata: ["exact", "boundary", "unicode", "normalization", "ambiguity"] },
  email_syntax: { id: "email_syntax", version: 1, country: "core", category: "personal", severity: "warning", changePolicy: "increment-on-behavior-change", evidenceKind: "pattern", requiredStrata: ["positive", "hard_negative", "boundary", "punctuation", "multiline", "prefix_suffix"] },
  iban_mod97: { id: "iban_mod97", version: 1, country: "core", category: "personal", severity: "critical", changePolicy: "increment-on-behavior-change", evidenceKind: "structural", requiredStrata: ["frozen_valid", "targeted_invalid", "format", "boundary"] },
  payment_card_luhn: { id: "payment_card_luhn", version: 1, country: "core", category: "personal", severity: "critical", changePolicy: "increment-on-behavior-change", evidenceKind: "structural", requiredStrata: ["frozen_valid", "targeted_invalid", "format", "boundary"] },
  bearer_token: { id: "bearer_token", version: 1, country: "core", category: "secret", severity: "critical", changePolicy: "increment-on-behavior-change", evidenceKind: "secret", requiredStrata: ["positive", "hard_negative", "code_config_negative"] },
  jwt_shape: { id: "jwt_shape", version: 1, country: "core", category: "secret", severity: "critical", changePolicy: "increment-on-behavior-change", evidenceKind: "secret", requiredStrata: ["positive", "hard_negative", "code_config_negative"] },
  secret_assignment: { id: "secret_assignment", version: 1, country: "core", category: "secret", severity: "critical", changePolicy: "increment-on-behavior-change", evidenceKind: "secret", requiredStrata: ["positive", "hard_negative", "code_config_negative"] },
  pem_private_key: { id: "pem_private_key", version: 1, country: "core", category: "secret", severity: "critical", changePolicy: "increment-on-behavior-change", evidenceKind: "secret", requiredStrata: ["positive", "hard_negative", "code_config_negative"] },
  se_personnummer: { id: "se_personnummer", version: 1, country: "se", category: "personal", severity: "critical", changePolicy: "increment-on-behavior-change", evidenceKind: "structural", requiredStrata: ["frozen_valid", "targeted_invalid", "format", "boundary", "century_separator"] },
  se_samordningsnummer: { id: "se_samordningsnummer", version: 1, country: "se", category: "personal", severity: "critical", changePolicy: "increment-on-behavior-change", evidenceKind: "structural", requiredStrata: ["frozen_valid", "targeted_invalid", "format", "boundary", "century_separator"] },
  dk_cpr_date: { id: "dk_cpr_date", version: 1, country: "dk", category: "personal", severity: "critical", changePolicy: "increment-on-behavior-change", evidenceKind: "structural", requiredStrata: ["frozen_valid", "targeted_invalid", "format", "boundary"] },
  lt_personal_code: { id: "lt_personal_code", version: 1, country: "lt", category: "personal", severity: "critical", changePolicy: "increment-on-behavior-change", evidenceKind: "structural", requiredStrata: ["frozen_valid", "targeted_invalid", "format", "boundary"] },
} as const satisfies Record<RuleId, RuleDefinition>;

export function rule(id: RuleId): RuleRef {
  const definition = RULE_CATALOG[id];
  return { id: definition.id, version: definition.version };
}

export function isExactRuleRef(value: RuleRef, expected: RuleRef): boolean {
  return value.id === expected.id && value.version === expected.version;
}
